<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    <main class="p-6">
      <div class="max-w-5xl mx-auto">
        <div class="flex items-center justify-between mb-6">
          <h1 class="text-2xl font-bold">API Keys</h1>
        </div>

        <ErrorMessage v-if="error" :message="error" title="Error" class="mb-8" />

        <div class="card mb-8">
          <div class="card-header">
            <h2 class="text-lg font-semibold">Create Key</h2>
          </div>
          <div class="card-body">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="form-label">Type</label>
                <select v-model="form.type" class="form-input">
                  <option value="tenant">Tenant</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div v-if="form.type==='tenant'">
                <label class="form-label">Tenant</label>
                <select v-model="form.tenantId" class="form-input">
                  <option value="" disabled>Select tenant</option>
                  <option v-for="t in adminStore.tenants" :key="t.tenantId" :value="t.tenantId">{{ t.name }}</option>
                </select>
                <div class="text-sm mt-1">
                  or
                  <router-link class="text-blue-600 underline" to="/admin/tenants">create a tenant</router-link>
                </div>
              </div>
              <div>
                <label class="form-label">Name</label>
                <input v-model="form.name" class="form-input" placeholder="Key name" />
              </div>
              <div>
                <label class="form-label">Description</label>
                <input v-model="form.description" class="form-input" placeholder="Optional description" />
              </div>
              <div>
                <label class="form-label">Expires At</label>
                <input v-model="form.expiresAt" type="datetime-local" class="form-input" />
              </div>
            </div>
            <div class="flex justify-end mt-4">
              <button class="btn btn-primary" @click="createKey" :disabled="isLoading">Create Key</button>
            </div>
            <div v-if="createdKey" class="mt-4">
              <div class="text-sm text-gray-600 mb-2">Copy and store this key now:</div>
              <div 
                @click="copyToClipboard(createdKey)"
                class="font-mono break-all p-2 rounded cursor-pointer shortcode-outline hover:bg-gray-200 transition-colors"
                :title="copyStatus"
              >
                {{ createdKey }}
              </div>
            </div>
          </div>
        </div>

        <div class="card mb-8">
          <div class="card-header">
            <h2 class="text-lg font-semibold">Tenant Keys</h2>
          </div>
          <div class="card-body p-0">
            <table class="table">
                                          <thead>
                 <tr>
                   <th>Name</th>
                   <th>Tenant</th>
                   <th>Description</th>
                   <th>Key Suffix</th>
                   <th>Status</th>
                   <th>Expiration</th>
                   <th>Actions</th>
                 </tr>
               </thead>
                           <tbody>
                                <tr v-for="k in tenantKeys" :key="k.keyId" @click="showKeyModal(k)" class="cursor-pointer hover:bg-gray-50">
                   <td>
                     <span class="text-blue-600 hover:text-blue-800 underline">
                       {{ k.name }}
                     </span>
                   </td>
                   <td>{{ getTenantName(k.tenantId) }}</td>
                   <td>{{ k.description }}</td>
                   <td>
                     <span class="font-mono text-xs text-gray-500">
                       {{ getKeyIdentifier(k) }}
                     </span>
                   </td>
                   <td>
                     <span :class="getStatusClass(k)" class="px-2 py-1 text-xs rounded-full">
                       {{ getStatusText(k) }}
                     </span>
                   </td>
                   <td>
                     <span v-if="k.expiresAt" :class="isExpired(k) ? 'text-red-600' : 'text-gray-600'" class="text-sm">
                       {{ formatExpiration(k.expiresAt) }}
                     </span>
                     <span v-else class="text-gray-400 text-sm">Never</span>
                   </td>
                   <td>
                     <div class="flex gap-2" @click.stop>
                       <button 
                         @click="toggleKeyStatus(k)" 
                         :class="k.isActive ? 'btn btn-warning btn-sm' : 'btn btn-success btn-sm'"
                         :disabled="isExpired(k)"
                       >
                         {{ k.isActive ? 'Deactivate' : 'Activate' }}
                       </button>
                       <button 
                         @click="purgeKey(k)" 
                         class="btn btn-danger btn-sm"
                       >
                         Purge
                       </button>
                     </div>
                   </td>
                 </tr>
             </tbody>
            </table>
          </div>
        </div>

        <div class="card mb-8">
          <div class="card-header">
            <h2 class="text-lg font-semibold">Admin Keys</h2>
          </div>
          <div class="card-body p-0">
            <table class="table">
                           <thead>
               <tr>
                 <th>Name</th>
                 <th>Description</th>
                 <th>Key Suffix</th>
                 <th>Status</th>
                 <th>Expiration</th>
                 <th>Actions</th>
               </tr>
             </thead>
                           <tbody>
                                <tr v-for="k in adminKeys" :key="k.keyId" @click="showKeyModal(k)" class="cursor-pointer hover:bg-gray-50">
                   <td>
                     <span class="text-blue-600 hover:text-blue-800 underline">
                       {{ k.name }}
                     </span>
                   </td>
                   <td>{{ k.description }}</td>
                   <td>
                     <span class="font-mono text-xs text-gray-500">
                       {{ getKeyIdentifier(k) }}
                     </span>
                   </td>
                   <td>
                     <span :class="getStatusClass(k)" class="px-2 py-1 text-xs rounded-full">
                       {{ getStatusText(k) }}
                     </span>
                   </td>
                   <td>
                     <span v-if="k.expiresAt" :class="isExpired(k) ? 'text-red-600' : 'text-gray-600'" class="text-sm">
                       {{ formatExpiration(k.expiresAt) }}
                     </span>
                     <span v-else class="text-gray-400 text-sm">Never</span>
                   </td>
                   <td>
                     <div class="flex gap-2" @click.stop>
                       <button 
                         @click="toggleKeyStatus(k)" 
                         :class="k.isActive ? 'btn btn-warning btn-sm' : 'btn btn-success btn-sm'"
                         :disabled="isExpired(k)"
                       >
                         {{ k.isActive ? 'Deactivate' : 'Activate' }}
                       </button>
                       <button 
                         @click="purgeKey(k)" 
                         class="btn btn-danger btn-sm"
                       >
                         Purge
                       </button>
                     </div>
                   </td>
                 </tr>
             </tbody>
            </table>
          </div>
        </div>

        <!-- API Key Modal -->
        <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div class="flex justify-between items-center mb-4">
              <h3 class="text-lg font-semibold">API Key Details</h3>
              <button @click="closeModal" class="text-gray-500 hover:text-gray-700">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div class="mb-4">
              <p class="text-sm text-gray-600 mb-2">Copy and store this key now:</p>
              <div class="flex items-center gap-2">
                <div class="font-mono break-all p-2 rounded flex-1 shortcode-outline">{{ selectedKey?.apiKey || 'Key not available' }}</div>
                <button 
                  v-if="selectedKey?.apiKey"
                  @click="copyToClipboard(selectedKey.apiKey)" 
                  class="btn btn-secondary px-3 py-2"
                  :title="copyStatus"
                >
                  <svg v-if="!copied" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                  <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </button>
              </div>
            </div>
            <div class="flex justify-end">
              <button @click="closeModal" class="btn btn-primary">Close</button>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Header from '@/components/common/Header.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import { useAdminStore } from '@/stores/admin'
import { adminService } from '@/services/admin'
import type { ApiKey } from '@/types'

const adminStore = useAdminStore()
const adminKeys = ref<ApiKey[]>([])
const tenantKeys = ref<ApiKey[]>([])
const createdKey = ref('')
const error = ref('')
const isLoading = ref(false)
const copied = ref(false)
const copyStatus = ref('Copy to clipboard')
const showModal = ref(false)
const selectedKey = ref<ApiKey & { apiKey?: string } | null>(null)

const form = ref({
  type: 'tenant' as 'tenant' | 'admin',
  tenantId: '',
  name: '',
  description: '',
  expiresAt: ''
})

const load = async () => {
  try {
    error.value = ''
    await adminStore.fetchTenants()
    adminKeys.value = await adminService.listAdminApiKeys()
    
    // Load tenant keys for all tenants
    const allTenantKeys: ApiKey[] = []
    for (const tenant of adminStore.tenants) {
      try {
        const keys = await adminService.listTenantApiKeys(tenant.tenantId)
        allTenantKeys.push(...keys)
      } catch (e) {
        console.warn(`Failed to load keys for tenant ${tenant.tenantId}:`, e)
      }
    }
    tenantKeys.value = allTenantKeys
  } catch (e: any) {
    error.value = e?.message || 'Failed to load keys'
  }
}

const createKey = async () => {
  try {
    isLoading.value = true
    createdKey.value = ''
    
    // Convert datetime-local to ISO string if provided
    let expiresAt: string | undefined
    if (form.value.expiresAt) {
      const date = new Date(form.value.expiresAt)
      expiresAt = date.toISOString()
    }
    
    if (form.value.type === 'admin') {
      const res = await adminService.createAdminApiKey({
        name: form.value.name,
        description: form.value.description || undefined,
        expiresAt
      })
      createdKey.value = res.apiKey
    } else {
      if (!form.value.tenantId) throw new Error('Select a tenant')
      const res = await adminService.createTenantApiKey(form.value.tenantId, {
        name: form.value.name,
        description: form.value.description || undefined,
        expiresAt
      })
      createdKey.value = res.apiKey
    }
    await load()
    
    // Clear form fields after successful creation
    form.value.name = ''
    form.value.description = ''
    form.value.expiresAt = ''
    if (form.value.type === 'tenant') {
      form.value.tenantId = ''
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to create key'
  } finally {
    isLoading.value = false
  }
}

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    copyStatus.value = 'Copied!'
    setTimeout(() => {
      copied.value = false
      copyStatus.value = 'Copy to clipboard'
    }, 2000)
  } catch (e) {
    console.error('Failed to copy to clipboard:', e)
    copyStatus.value = 'Failed to copy'
  }
}

const getTenantName = (tenantId: string | undefined) => {
  if (!tenantId) return 'Unknown'
  const tenant = adminStore.tenants.find(t => t.tenantId === tenantId)
  return tenant?.name || tenantId
}

const isExpired = (key: ApiKey): boolean => {
  return !!(key.expiresAt && key.expiresAt < Date.now())
}

const getStatusClass = (key: ApiKey): string => {
  if (isExpired(key)) return 'bg-red-100 text-red-800'
  if (key.isActive) return 'bg-green-100 text-green-800'
  return 'bg-gray-100 text-gray-800'
}

const getStatusText = (key: ApiKey): string => {
  if (isExpired(key)) return 'Expired'
  if (key.isActive) return 'Active'
  return 'Inactive'
}

const formatExpiration = (expiresAt: number): string => {
  const date = new Date(expiresAt)
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

const getKeyIdentifier = (key: ApiKey): string => {
  if (key.keySuffix) {
    return key.keySuffix // Actual API key suffix
  } else {
    return key.keyId.slice(-8) + ' (ID)' // Fallback with indicator
  }
}

const showKeyModal = (key: ApiKey) => {
  selectedKey.value = key
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  selectedKey.value = null
}

const toggleKeyStatus = async (key: ApiKey) => {
  try {
    isLoading.value = true
    const newStatus = !key.isActive
    
    if (key.isAdmin) {
      await adminService.updateAdminApiKey(key.keyId, newStatus)
    } else {
      if (!key.tenantId) return
      await adminService.updateTenantApiKey(key.tenantId, key.keyId, newStatus)
    }
    await load()
  } catch (e: any) {
    error.value = e?.message || 'Failed to toggle key status'
  } finally {
    isLoading.value = false
  }
}

const purgeKey = async (key: ApiKey) => {
  if (!confirm('Are you sure you want to permanently delete this API key? This action cannot be undone.')) {
    return
  }
  
  try {
    isLoading.value = true
    if (key.isAdmin) {
      await adminService.deleteAdminApiKey(key.keyId)
    } else {
      if (!key.tenantId) return
      await adminService.deleteTenantApiKey(key.tenantId, key.keyId)
    }
    await load()
  } catch (e: any) {
    error.value = e?.message || 'Failed to delete key'
  } finally {
    isLoading.value = false
  }
}

onMounted(load)
</script>



